import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IntegrationMessageDTO } from '../../../api/api';
import { SearchIcon } from '../../../assets/icons/SearchIcon';
import Table from '../../../blocks/table/TableClient';
import { Select, MenuItem } from '../../../components/select/Select';
import TextField from '../../../components/text-field/TextField';
import Typography from '../../../components/typography/Typography';
import AdminService from '../../../services/AdminService';
import NotificationService from '../../../services/NotificationService';
import { log } from '../../../utils/logging/log';
import IntegrationMessageDialog from './IntegrationMessageDialog';
import useIntegrationMessagesList, { IntegrationMessageData, MessageBox } from './useIntegrationMessagesList';

interface Props {
  box: MessageBox;
  title: string;
  description: string;
}

const TAKE_OPTIONS = [100, 500, 1000, 5000];
const SEARCH_DEBOUNCE_MS = 500;

/**
 * The Inbox and the Outbox pages. Both read the same shaped rows from TaskService, so
 * the only thing that differs is which endpoint is called and whether the correlation id
 * is shown.
 */
const IntegrationMessagesView = (props: Props) => {
  const { box, title, description } = props;

  const [messages, setMessages] = useState<IntegrationMessageDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [take, setTake] = useState(TAKE_OPTIONS[1]);
  const [selectedMessage, setSelectedMessage] = useState<IntegrationMessageDTO>();

  // The database search hits the payload column, so it waits for a pause in the typing.
  useEffect(() => {
    const timeout = setTimeout(() => setSearchText(searchInput.trim()), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  // Typing in the search field or changing the count restarts the fetch, so the
  // previous one is cancelled to keep a slow response from overwriting a newer one.
  const controllerRef = useRef<AbortController | null>(null);

  const fetchMessages = useCallback(() => {
    setLoading(true);

    controllerRef.current?.abort();
    controllerRef.current = new AbortController();

    const request =
      box === 'inbox'
        ? AdminService.getInboxMessages(searchText || undefined, take, controllerRef.current)
        : AdminService.getOutboxMessages(searchText || undefined, take, controllerRef.current);

    request
      .then((res) => {
        setMessages(res ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;

        log(err);
        NotificationService.error(`Kunne ikke hente beskeder fra ${title}`);
        setLoading(false);
      });
  }, [box, searchText, take, title]);

  useEffect(() => {
    fetchMessages();

    return () => controllerRef.current?.abort();
  }, [fetchMessages]);

  const { tableInstance } = useIntegrationMessagesList(messages, box);
  const { setAllFilters } = tableInstance;

  const handleClickRow = useCallback((rowData: IntegrationMessageData) => {
    setSelectedMessage(rowData.message);
  }, []);

  const handleClearFilters = useCallback(() => setAllFilters([]), [setAllFilters]);

  return (
    <Container>
      <Header>
        <div>
          <Typography variant="h3">{title}</Typography>
          <Typography variant="span" color="darkGrey">
            {description}
          </Typography>
        </div>
        <Tools>
          <TextField
            label="Søg i databasen"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            helperText="Id, type, payload, fejlbesked, correlation id"
            width="260px"
            InputProps={{ startAdornment: <SearchIcon size="16px" margin="0 8px 0 0" /> }}
          />
          <TakeSelect>
            <Select
              label="Antal"
              value={take}
              onChange={(e) => setTake(Number(e.target.value))}
              fullWidth
            >
              {TAKE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {`Nyeste ${option}`}
                </MenuItem>
              ))}
            </Select>
          </TakeSelect>
        </Tools>
      </Header>
      <Table
        loading={loading}
        tableInstance={tableInstance}
        onClickRow={handleClickRow}
        noDataText={searchText ? 'Ingen beskeder matcher søgningen' : 'Ingen beskeder'}
        multipleTypeName="beskeder"
        showPagination
        showCount
        showColumnFiltering
        alwaysShowSort
        showClearFilter
        showUpdateTable
        onClearFilters={handleClearFilters}
        onUpdateTable={fetchMessages}
      />
      {selectedMessage && (
        <IntegrationMessageDialog
          message={selectedMessage}
          box={box}
          handleClose={() => setSelectedMessage(undefined)}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  text-align: start;
  height: 100%;

  & > *:nth-child(1) {
    padding: 30px 30px 0px 30px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  column-gap: ${(props) => props.theme.spacing(4)};
`;

const Tools = styled.div`
  display: flex;
  align-items: flex-start;
  column-gap: ${(props) => props.theme.spacing(3)};
`;

const TakeSelect = styled.div`
  min-width: 150px;
`;

export default IntegrationMessagesView;
